import type { ComponentType } from "react"
import { createBrowserRouter, Navigate, useSearchParams, type RouteObject } from "react-router-dom"

import { AppShell } from "@/components/layout/app-shell"
import { AuthLayout } from "@/components/layout/auth-layout"
import { RequireAuth } from "@/components/layout/require-auth"
import { RootLayout } from "@/components/layout/root-layout"
import type { RouteHandle } from "@/components/layout/route-handle"
import { NotFound } from "@/components/shared/not-found"
import { RouteError } from "@/components/shared/route-error"

/** Code-splits a page: `page(() => import("./x"), "XRoute")`. */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, exportName: keyof M & string) {
  return async () => ({ Component: (await load())[exportName] as ComponentType })
}

const handle = (title: string, crumb: string = title): RouteHandle => ({ title, crumb })

/** AI Studio was split into Trend Explorer (discover), New AI post (write) and AI Generations (review): old links land
 * on the page that now does what their tab did. */
const AI_STUDIO_TABS: Record<string, string> = {
  manual: "/dashboard/ai-generations/new",
  auto: "/dashboard/ai-generations/new",
  drafts: "/dashboard/ai-generations?state=review",
  history: "/dashboard/ai-generations?state=all",
  logs: "/dashboard/ai-generations?state=all&log=1",
  trends: "/dashboard/trends",
}

function AiStudioRedirect() {
  const [params] = useSearchParams()
  return <Navigate to={AI_STUDIO_TABS[params.get("tab") ?? ""] ?? "/dashboard/trends"} replace />
}

/** Standard list / new / edit route trio for a CRUD resource. */
function resourceRoutes(
  path: string,
  label: string,
  singular: string,
  pages: { list: RouteObject["lazy"]; create: RouteObject["lazy"]; edit: RouteObject["lazy"] },
): RouteObject {
  return {
    path,
    handle: { crumb: label } satisfies RouteHandle,
    children: [
      { index: true, handle: { title: label } satisfies RouteHandle, lazy: pages.list },
      { path: "new", handle: { ...handle(`New ${singular}`, "New"), hideMobileNav: true }, lazy: pages.create },
      { path: ":id/edit", handle: { ...handle(`Edit ${singular}`, "Edit"), hideMobileNav: true }, lazy: pages.edit },
    ],
  }
}

const dashboardRoutes: RouteObject[] = [
  { index: true, handle: { title: "Dashboard" } satisfies RouteHandle, lazy: page(() => import("@/routes/dashboard/overview"), "DashboardOverview") },
  resourceRoutes("posts", "Posts", "post", {
    list: page(() => import("@/routes/posts/list"), "PostsListRoute"),
    create: page(() => import("@/routes/posts/new"), "PostNewRoute"),
    edit: page(() => import("@/routes/posts/edit"), "PostEditRoute"),
  }),
  resourceRoutes("categories", "Categories", "category", {
    list: page(() => import("@/routes/categories/list"), "CategoriesRoute"),
    create: page(() => import("@/routes/categories/new"), "CategoryNewRoute"),
    edit: page(() => import("@/routes/categories/edit"), "CategoryEditRoute"),
  }),
  resourceRoutes("subcategories", "Subcategories", "subcategory", {
    list: page(() => import("@/routes/subcategories/list"), "SubCategoriesListRoute"),
    create: page(() => import("@/routes/subcategories/new"), "SubCategoryNewRoute"),
    edit: page(() => import("@/routes/subcategories/edit"), "SubCategoryEditRoute"),
  }),
  resourceRoutes("tags", "Tags", "tag", {
    list: page(() => import("@/routes/tags/list"), "TagsRoute"),
    create: page(() => import("@/routes/tags/new"), "TagNewRoute"),
    edit: page(() => import("@/routes/tags/edit"), "TagEditRoute"),
  }),
  resourceRoutes("authors", "Authors", "author", {
    list: page(() => import("@/routes/authors/list"), "AuthorsRoute"),
    create: page(() => import("@/routes/authors/new"), "AuthorNewRoute"),
    edit: page(() => import("@/routes/authors/edit"), "AuthorEditRoute"),
  }),
  { path: "ai-posts", element: <AiStudioRedirect /> },
  {
    path: "ai-generations",
    handle: { crumb: "AI Generations" } satisfies RouteHandle,
    children: [
      { index: true, handle: { title: "AI Generations" } satisfies RouteHandle, lazy: page(() => import("@/routes/ai-generations/list"), "AiGenerationsRoute") },
      { path: "new", handle: handle("New AI post", "New post"), lazy: page(() => import("@/routes/ai-generations/new"), "NewAiPostRoute") },
      { path: ":id", handle: handle("Review generation", "Review"), lazy: page(() => import("@/routes/ai-generations/review"), "AiGenerationReviewRoute") },
    ],
  },
  { path: "trends", handle: handle("Trend Explorer"), lazy: page(() => import("@/routes/trends"), "TrendExplorerRoute") },
  { path: "calendar", handle: handle("Content calendar", "Calendar"), lazy: page(() => import("@/routes/calendar"), "ContentCalendarRoute") },
  { path: "seo", handle: handle("SEO"), lazy: page(() => import("@/routes/seo"), "SeoRoute") },
  { path: "settings", handle: handle("Settings"), lazy: page(() => import("@/routes/settings"), "SettingsRoute") },
  { path: "*", handle: handle("Not found"), element: <NotFound /> },
]

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/",
        element: <AuthLayout />,
        errorElement: <RouteError />,
        children: [{ index: true, handle: { title: "Sign in" } satisfies RouteHandle, lazy: page(() => import("@/routes/login"), "LoginRoute") }],
      },
      {
        path: "/dashboard",
        handle: { crumb: "Dashboard" } satisfies RouteHandle,
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          // Errors inside a page render within the shell so navigation stays usable.
          { errorElement: <RouteError />, children: dashboardRoutes },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
})
